import { useEffect, useState } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { Laptop, Smartphone, MonitorSmartphone, Trash2 } from 'lucide-react';

function detectIcon(ua = '') {
  const u = (ua || '').toLowerCase();
  if (/mobile|iphone|android/.test(u)) return Smartphone;
  if (/ipad|tablet/.test(u)) return MonitorSmartphone;
  return Laptop;
}
function prettyUA(ua = '') {
  if (!ua) return 'Unknown device';
  const b = /Chrome/i.test(ua) ? 'Chrome' : /Firefox/i.test(ua) ? 'Firefox' :
            /Edg/i.test(ua) ? 'Edge' : /Safari/i.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/i.test(ua) ? 'Windows' : /Mac OS X/i.test(ua) ? 'macOS' :
             /iPhone|iOS/i.test(ua) ? 'iOS' : /Android/i.test(ua) ? 'Android' :
             /Linux/i.test(ua) ? 'Linux' : 'Unknown';
  return `${b} on ${os}`;
}
function daysUntil(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  const d = Math.max(0, Math.round(ms / 86400000));
  return `${d}d left`;
}

export default function TrustedDevicesList() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get('/auth/2fa/trusted-devices');
      setDevices(data.devices || []);
    } catch { /* noop — non-2FA users get 200 with empty list */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    if (!window.confirm('Revoke this trusted device? It will need a fresh 2FA code next login.')) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await http.delete(`/auth/2fa/trusted-devices/${id}`);
      toast.success('Trusted device revoked.');
      await load();
    } catch { toast.error('Could not revoke that device.'); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  if (loading) return null;
  if (devices.length === 0) return null;

  return (
    <div className="bg-white rounded-3xl p-6 card-soft" data-testid="trusted-devices-card">
      <h3 className="font-heading text-lg">Trusted devices</h3>
      <p className="text-sm text-navy/60 mt-1">Devices that skip the 2FA prompt for 30 days.</p>
      <div className="mt-4 space-y-2">
        {devices.map((d) => {
          const Icon = detectIcon(d.user_agent);
          return (
            <div key={d.id} className="flex items-center gap-3 rounded-2xl bg-beige/60 px-4 py-3" data-testid={`trusted-device-${d.id}`}>
              <Icon size={16} className={d.is_current ? 'text-emerald-700' : 'text-navy/60'}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium truncate">{prettyUA(d.user_agent)}</span>
                  {d.is_current && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
                      This device
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-navy/50">{daysUntil(d.expires_at)}</p>
              </div>
              <button onClick={() => revoke(d.id)} disabled={busy[d.id]}
                className="text-xs bg-white text-red-700 rounded-full px-3 py-1 hover:bg-red-50 border border-red-200 disabled:opacity-50"
                data-testid={`trusted-device-revoke-${d.id}`}>
                <Trash2 size={12} className="inline mr-1"/> Revoke
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
